import {
  collection,
  getDocs,
  getDoc,
  doc,
  setDoc,
  deleteDoc,
  query,
  onSnapshot,
  Unsubscribe,
  serverTimestamp,
  arrayUnion
} from 'firebase/firestore';
import { db, auth, sanitizeFirestoreData } from './firebase';
import { Product } from '../types';
import initialProductsData from '../data/initialProducts.json';
import { perfTracker } from './perfLogger';
import { hasProductRealImage, KNOWN_DELETED_IDS, getImageDeduplicationKey } from './productImages';

// Master base catalog: strictly filtered to products that have valid media and are not deleted
const INITIAL_PRODUCTS = (initialProductsData as unknown as Product[]).filter(p => {
  return p && p.id && !KNOWN_DELETED_IDS.includes(p.id) && !(p as any).isDeleted && p.status !== 'deleted' && hasProductRealImage(p);
});

const PRODUCTS_COLLECTION = 'products';
const CACHE_KEY = 'rh_cached_products';
const DELETED_CACHE_KEY = 'rh_deleted_product_ids';
const SYNC_CHANNEL_NAME = 'rh_products_realtime_sync';

// Cross-Tab Real-time Synchronizer
let syncChannel: BroadcastChannel | null = null;
if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
  try {
    syncChannel = new BroadcastChannel(SYNC_CHANNEL_NAME);
    syncChannel.onmessage = (event) => {
      if (event.data && event.data.type === 'PRODUCTS_MUTATED' && Array.isArray(event.data.products)) {
        lastProductData = event.data.products;
        safeCacheProducts(event.data.products);
        productSubscribers.forEach(cb => {
          try { cb(event.data.products, 'server'); } catch (e) {}
        });
      }
    };
  } catch {}
}

export function broadcastProductsMutation(prods: Product[]) {
  if (syncChannel) {
    try {
      syncChannel.postMessage({ type: 'PRODUCTS_MUTATED', products: prods });
    } catch {}
  }
}

const firestoreDeletedIds = new Set<string>(KNOWN_DELETED_IDS);

export function getDeletedProductIds(): Set<string> {
  const set = new Set<string>(firestoreDeletedIds);
  try {
    if (typeof window !== 'undefined') {
      const raw = localStorage.getItem(DELETED_CACHE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) arr.forEach(id => set.add(id));
      }
    }
  } catch {}
  return set;
}

export function markProductAsDeleted(id: string) {
  if (!id) return;
  firestoreDeletedIds.add(id);
  try {
    if (typeof window !== 'undefined') {
      const set = getDeletedProductIds();
      set.add(id);
      localStorage.setItem(DELETED_CACHE_KEY, JSON.stringify(Array.from(set)));

      const purgeFromStorage = (key: string) => {
        try {
          const raw = localStorage.getItem(key);
          if (raw) {
            const arr = JSON.parse(raw);
            if (Array.isArray(arr)) {
              const cleaned = arr.filter((p: any) => p && p.id !== id);
              localStorage.setItem(key, JSON.stringify(cleaned));
            }
          }
        } catch {}
      };
      purgeFromStorage(CACHE_KEY);
      purgeFromStorage('rh_cached_products');
    }
  } catch {}
}

export function unmarkProductAsDeleted(id: string) {
  if (!id) return;
  firestoreDeletedIds.delete(id);
  try {
    if (typeof window !== 'undefined') {
      const set = getDeletedProductIds();
      if (set.has(id)) {
        set.delete(id);
        localStorage.setItem(DELETED_CACHE_KEY, JSON.stringify(Array.from(set)));
      }
    }
  } catch {}
}

// Safely cache products without exceeding browser localStorage quota
export function safeCacheProducts(prods: Product[]) {
  if (!prods || !Array.isArray(prods) || prods.length === 0) return;
  try {
    const deletedIds = getDeletedProductIds();
    const lightweight = prods
      .filter(p => p && p.id && !deletedIds.has(p.id) && hasProductRealImage(p))
      .map(p => {
        const previewImages = Array.isArray(p.previewImages)
          ? p.previewImages.map((img, i) =>
              typeof img === 'string' && img.startsWith('data:image/')
                ? `/api/product-image/${p.id}/${i}`
                : img
            )
          : [];
        return {
          ...p,
          previewImages,
          image: previewImages[0] || (typeof p.image === 'string' && p.image.startsWith('data:image/') ? `/api/product-image/${p.id}/0` : p.image) || ''
        };
      });
    localStorage.setItem(CACHE_KEY, JSON.stringify(lightweight));
    localStorage.setItem('rh_cached_products', JSON.stringify(lightweight));
  } catch (e) {
    // Graceful fallback
  }
}

// Read local cache immediately for 0ms initial state
export function getLocalCachedProducts(): Product[] {
  const deletedIds = getDeletedProductIds();
  const catalogMap = new Map<string, Product>();

  // 1. Immutable base catalog
  INITIAL_PRODUCTS.forEach(p => {
    if (p && p.id && !deletedIds.has(p.id) && !KNOWN_DELETED_IDS.includes(p.id) && !(p as any).isDeleted && p.status !== 'deleted') {
      catalogMap.set(p.id, p);
    }
  });

  // 2. Overlay any cached edits/custom products from localStorage
  try {
    const cached = localStorage.getItem(CACHE_KEY) || localStorage.getItem('rh_cached_products');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (Array.isArray(parsed) && parsed.length > 0) {
        parsed.forEach(p => {
          if (p && p.id) {
            if (deletedIds.has(p.id) || KNOWN_DELETED_IDS.includes(p.id) || p.isDeleted === true || p.status === 'deleted') {
              catalogMap.delete(p.id);
            } else {
              const existing = catalogMap.get(p.id);
              catalogMap.set(p.id, existing ? { ...existing, ...p } : p);
            }
          }
        });
      }
    }
  } catch (e) {
    // Fallback to initial products
  }

  return Array.from(catalogMap.values()).filter(p => p && p.id && !deletedIds.has(p.id) && !KNOWN_DELETED_IDS.includes(p.id) && !(p as any).isDeleted && p.status !== 'deleted');
}

// Master Catalog Merger: Guarantees live edits overlay base products, preserving live Firestore order
export function mergeWithBaseCatalog(fetchedDocs: Product[]): Product[] {
  const deletedIds = getDeletedProductIds();
  const catalogMap = new Map<string, Product>();

  // If live docs are fetched, they take primary precedence preserving live order
  if (Array.isArray(fetchedDocs) && fetchedDocs.length > 0) {
    fetchedDocs.forEach(p => {
      if (!p || !p.id) return;
      if (deletedIds.has(p.id) || KNOWN_DELETED_IDS.includes(p.id) || (p as any).isDeleted === true || p.status === 'deleted') {
        markProductAsDeleted(p.id);
        return;
      }
      if (!p.image && Array.isArray(p.previewImages) && p.previewImages.length > 0) {
        p.image = p.previewImages[0];
      }
      if (p.image && (!Array.isArray(p.previewImages) || p.previewImages.length === 0)) {
        p.previewImages = [p.image];
      }
      catalogMap.set(p.id, p);
    });

    // Merge in any base catalog products not already in fetched docs
    INITIAL_PRODUCTS.forEach(p => {
      if (p && p.id && !catalogMap.has(p.id) && !deletedIds.has(p.id) && !KNOWN_DELETED_IDS.includes(p.id) && !(p as any).isDeleted && p.status !== 'deleted') {
        catalogMap.set(p.id, p);
      }
    });

    return Array.from(catalogMap.values()).filter(p => p && p.id && !deletedIds.has(p.id) && !KNOWN_DELETED_IDS.includes(p.id) && !(p as any).isDeleted && p.status !== 'deleted');
  }

  // Fallback if no fetched docs: use base catalog
  INITIAL_PRODUCTS.forEach(p => {
    if (p && p.id && !deletedIds.has(p.id) && !KNOWN_DELETED_IDS.includes(p.id) && !(p as any).isDeleted && p.status !== 'deleted') {
      catalogMap.set(p.id, p);
    }
  });

  return Array.from(catalogMap.values()).filter(p => p && p.id && !deletedIds.has(p.id) && !KNOWN_DELETED_IDS.includes(p.id) && !(p as any).isDeleted && p.status !== 'deleted');
}

function parseFirestoreRestDoc(doc: any): Product | null {
  try {
    const id = doc.name ? doc.name.split('/').pop() : '';
    if (!id) return null;

    // Tombstone document recognition
    if (id === '_catalog_tombstones' || id === 'catalog_tombstones') {
      const arr = doc.fields?.deletedIds?.arrayValue?.values || [];
      arr.forEach((v: any) => {
        const val = v?.stringValue || v;
        if (val) markProductAsDeleted(String(val));
      });
      return null;
    }

    if (id.startsWith('_') || !doc.fields) return null;
    const f = doc.fields;

    const getBool = (field: any) => field?.booleanValue ?? false;
    const getString = (field: any) => field?.stringValue || '';

    // If document is marked deleted or status is deleted, register tombstone immediately
    const isDeletedDoc = getBool(f.isDeleted) || getString(f.status).toLowerCase() === 'deleted';
    if (isDeletedDoc) {
      markProductAsDeleted(id);
      return null;
    }

    const title = getString(f.title) || getString(f.name);
    if (!title) return null;
    const getNum = (field: any) => (field?.doubleValue !== undefined ? Number(field.doubleValue) : (field?.integerValue !== undefined ? Number(field.integerValue) : 0));
    const getArr = (field: any) => (field?.arrayValue?.values || []).map((v: any) => v.stringValue || v.integerValue || v);

    const rawPreviewImages = getArr(f.previewImages);
    if (rawPreviewImages.length === 0) {
      const imgs = getArr(f.images);
      if (imgs.length > 0) rawPreviewImages.push(...imgs);
      const singleImg = getString(f.image) || getString(f.previewUrl);
      if (singleImg) {
        rawPreviewImages.push(singleImg);
      }
    }

    const seenMapKeys = new Set<string>();
    const previewImages: string[] = [];
    for (const url of rawPreviewImages) {
      if (typeof url === 'string' && url.trim()) {
        const clean = url.trim();
        const key = getImageDeduplicationKey(clean);
        if (!seenMapKeys.has(key)) {
          seenMapKeys.add(key);
          previewImages.push(clean);
        }
      }
    }

    const getDate = (field: any): string => {
      if (!field) return '';
      if (field.timestampValue) return field.timestampValue;
      if (field.stringValue) return field.stringValue;
      if (field.integerValue) return new Date(Number(field.integerValue)).toISOString();
      return '';
    };

    const createdAt = getDate(f.createdAt) || (doc.createTime ? new Date(doc.createTime).toISOString() : '');
    const updatedAt = getDate(f.updatedAt) || (doc.updateTime ? new Date(doc.updateTime).toISOString() : createdAt);

    return {
      id,
      title: getString(f.title) || getString(f.name) || id,
      name: getString(f.name) || getString(f.title) || id,
      description: getString(f.description),
      price: getNum(f.price),
      originalPrice: getNum(f.originalPrice) || getNum(f.price),
      discountPercent: getNum(f.discountPercent),
      category: getString(f.category) as any,
      country: getString(f.country) || getString(f.country_id),
      country_id: getString(f.country_id) || getString(f.country),
      status: getString(f.status) || 'published',
      formats: getArr(f.formats),
      previewImages,
      image: previewImages[0] || getString(f.image) || '',
      images: previewImages,
      tags: getArr(f.tags),
      rating: getNum(f.rating) || 5.0,
      reviewCount: getNum(f.reviewCount) || 12,
      salesCount: getNum(f.salesCount) || 0,
      isPopular: getBool(f.isPopular),
      isTrending: getBool(f.isTrending),
      isLatest: getBool(f.isLatest),
      hasSecureFile: getBool(f.hasSecureFile),
      downloadFileUrl: getString(f.downloadFileUrl),
      createdAt,
      updatedAt
    } as unknown as Product;
  } catch {
    return null;
  }
}

async function fetchProductsViaRest(): Promise<Product[]> {
  // 1. Direct Express /api/products backend proxy (pre-synced with full 200+ product catalog, ~20ms response)
  try {
    const apiRes = await fetch('/api/products');
    if (apiRes.ok) {
      const data = await apiRes.json();
      if (data && data.success && Array.isArray(data.products) && data.products.length > 0) {
        const deletedIds = getDeletedProductIds();
        const valid = data.products.filter((p: any) => p && p.id && !deletedIds.has(p.id) && !KNOWN_DELETED_IDS.includes(p.id) && !p.isDeleted && p.status !== 'deleted');
        if (valid.length > 0) {
          return valid;
        }
      }
    }
  } catch (apiErr) {
    // If backend proxy fails, proceed to fallback
  }

  // 2. Authoritative sync of tombstone IDs from backend and Firestore
  try {
    const res = await fetch('/api/deleted-product-ids');
    if (res.ok) {
      const data = await res.json();
      if (data && Array.isArray(data.deletedIds)) {
        data.deletedIds.forEach((id: string) => markProductAsDeleted(id));
      }
    }
  } catch {}

  try {
    const r1 = await fetch('https://firestore.googleapis.com/v1/projects/rh-editography-e0d61/databases/(default)/documents/products/_catalog_tombstones');
    if (r1.ok) {
      const d1 = await r1.json();
      const arr = d1?.fields?.deletedIds?.arrayValue?.values || [];
      arr.forEach((v: any) => {
        const id = v?.stringValue || v;
        if (id) markProductAsDeleted(String(id));
      });
    }
  } catch {}

  try {
    const r2 = await fetch('https://firestore.googleapis.com/v1/projects/rh-editography-e0d61/databases/(default)/documents/system_flags/catalog_tombstones');
    if (r2.ok) {
      const d2 = await r2.json();
      const arr2 = d2?.fields?.deletedIds?.arrayValue?.values || [];
      arr2.forEach((v: any) => {
        const id = v?.stringValue || v;
        if (id) markProductAsDeleted(String(id));
      });
    }
  } catch {}

  try {
    const list: Product[] = [];
    let pageToken = '';
    const fieldsMask = [
      'mask.fieldPaths=title',
      'mask.fieldPaths=name',
      'mask.fieldPaths=description',
      'mask.fieldPaths=price',
      'mask.fieldPaths=originalPrice',
      'mask.fieldPaths=discountPercent',
      'mask.fieldPaths=category',
      'mask.fieldPaths=country',
      'mask.fieldPaths=country_id',
      'mask.fieldPaths=status',
      'mask.fieldPaths=isDeleted',
      'mask.fieldPaths=formats',
      'mask.fieldPaths=tags',
      'mask.fieldPaths=rating',
      'mask.fieldPaths=reviewCount',
      'mask.fieldPaths=salesCount',
      'mask.fieldPaths=isPopular',
      'mask.fieldPaths=isTrending',
      'mask.fieldPaths=isLatest',
      'mask.fieldPaths=hasSecureFile',
      'mask.fieldPaths=downloadFileUrl',
      'mask.fieldPaths=createdAt',
      'mask.fieldPaths=updatedAt',
      'mask.fieldPaths=specs',
      'mask.fieldPaths=detailedFeatures',
      'mask.fieldPaths=previewUrl',
      'mask.fieldPaths=deletedIds',
      'mask.fieldPaths=previewCount'
    ].join('&');

    do {
      const url = `https://firestore.googleapis.com/v1/projects/rh-editography-e0d61/databases/(default)/documents/products?pageSize=300&${fieldsMask}${pageToken ? `&pageToken=${encodeURIComponent(pageToken)}` : ''}`;
      const res = await fetch(url);
      if (!res.ok) break;
      const data = await res.json();
      if (data.documents && Array.isArray(data.documents)) {
        for (const d of data.documents) {
          const parsed = parseFirestoreRestDoc(d);
          if (parsed) list.push(parsed);
        }
      }
      pageToken = data.nextPageToken || '';
    } while (pageToken);
    const deletedIds = getDeletedProductIds();
    return list.filter(p => p && p.id && !deletedIds.has(p.id) && !(p as any).isDeleted && p.status !== 'deleted');
  } catch {
    return [];
  }
}

let productSubscribers: Set<(products: Product[], source: 'cache' | 'server') => void> = new Set();
let lastProductData: Product[] | null = INITIAL_PRODUCTS;

function normalizeProductDoc(id: string, data: any): Product | null {
  if (!id || id.startsWith('_') || !data || data.isDeleted === true || data.status === 'deleted') return null;
  if (!data.title && !data.name) return null;
  const mapped = { ...data };
  // Extract dates reliably
  if (mapped.createdAt && typeof mapped.createdAt.toDate === 'function') {
    mapped.createdAt = mapped.createdAt.toDate().toISOString();
  } else if (mapped.createdAt && typeof mapped.createdAt.seconds === 'number') {
    mapped.createdAt = new Date(mapped.createdAt.seconds * 1000).toISOString();
  } else if (mapped.createdAt && typeof mapped.createdAt === 'string') {
    mapped.createdAt = mapped.createdAt;
  }

  if (mapped.updatedAt && typeof mapped.updatedAt.toDate === 'function') {
    mapped.updatedAt = mapped.updatedAt.toDate().toISOString();
  } else if (mapped.updatedAt && typeof mapped.updatedAt.seconds === 'number') {
    mapped.updatedAt = new Date(mapped.updatedAt.seconds * 1000).toISOString();
  } else if (mapped.updatedAt && typeof mapped.updatedAt === 'string') {
    mapped.updatedAt = mapped.updatedAt;
  }

  // Ensure previewImages has all available images
  if (!Array.isArray(mapped.previewImages) || mapped.previewImages.length === 0) {
    if (Array.isArray(mapped.images) && mapped.images.length > 0) {
      mapped.previewImages = mapped.images;
    } else if (mapped.image && typeof mapped.image === 'string') {
      mapped.previewImages = [mapped.image];
    } else if (mapped.previewUrl && typeof mapped.previewUrl === 'string') {
      mapped.previewImages = [mapped.previewUrl];
    } else {
      mapped.previewImages = [];
    }
  }

  // Ensure primary image field is populated from previewImages
  if (!mapped.image && Array.isArray(mapped.previewImages) && mapped.previewImages.length > 0) {
    mapped.image = mapped.previewImages[0];
  }

  return { id, ...mapped } as Product;
}

export async function fetchProductsFromFirestore(): Promise<Product[]> {
  const startTime = performance.now();
  try {
    const restProducts = await fetchProductsViaRest();
    const duration = performance.now() - startTime;
    const merged = mergeWithBaseCatalog(restProducts);
    perfTracker.logFetch(PRODUCTS_COLLECTION, duration, 'server', merged.length);
    safeCacheProducts(merged);
    return merged;
  } catch (error) {
    console.warn('Lightweight catalog fetch note, using local base catalog:', error);
    return mergeWithBaseCatalog([]);
  }
}

export function subscribeToProductsInFirestore(
  onUpdate: (products: Product[], source: 'cache' | 'server') => void,
  onError?: (error: Error) => void
): () => void {
  // 1. Deliver cached or initial catalog immediately (0ms response time)
  const cached = (lastProductData && lastProductData.length > 0) ? lastProductData : getLocalCachedProducts();
  if (cached && cached.length > 0) {
    onUpdate(cached, 'cache');
  }

  productSubscribers.add(onUpdate);

  let isCancelled = false;
  let safetyTimer: ReturnType<typeof setTimeout> | null = null;
  let isHydrated = (lastProductData && lastProductData.length > 0);

  // 2. Fetch authoritative catalog via lightweight /api/products backend proxy
  fetchProductsViaRest().then((restProds) => {
    if (isCancelled) return;
    if (safetyTimer) {
      clearTimeout(safetyTimer);
      safetyTimer = null;
    }
    if (restProds && restProds.length > 0) {
      isHydrated = true;
      const merged = mergeWithBaseCatalog(restProds);
      lastProductData = merged;
      safeCacheProducts(merged);
      broadcastProductsMutation(merged);
      productSubscribers.forEach((cb) => {
        try { cb(merged, 'server'); } catch (e) {}
      });
    }
  }).catch((err) => {
    if (isCancelled) return;
    console.warn('REST catalog sync note, checking error recovery:', err);
    // If REST failed and we have no products in memory at all, execute defensive fallback immediately
    if (!lastProductData || lastProductData.length === 0) {
      fetchProductsFromFirestore().then((directProducts) => {
        if (isCancelled) return;
        if (directProducts && directProducts.length > 0) {
          isHydrated = true;
          lastProductData = directProducts;
          safeCacheProducts(directProducts);
          broadcastProductsMutation(directProducts);
          productSubscribers.forEach(cb => {
            try { cb(directProducts, 'server'); } catch (e) {}
          });
        }
      }).catch(e => {
        if (onError) onError(e);
      });
    }
  });

  // 3. Defensive safety timer: ONLY triggers if REST request timed out AND memory is completely empty
  // (Prevents duplicate full-collection fetch when catalog data is already present or in flight)
  safetyTimer = setTimeout(async () => {
    if (isCancelled || isHydrated) return;
    if (!lastProductData || lastProductData.length === 0) {
      try {
        console.warn('Safety timer activating defensive Firestore fallback...');
        const directProducts = await fetchProductsFromFirestore();
        if (isCancelled) return;
        if (directProducts && directProducts.length > 0) {
          isHydrated = true;
          lastProductData = directProducts;
          safeCacheProducts(directProducts);
          broadcastProductsMutation(directProducts);
          productSubscribers.forEach(cb => {
            try { cb(directProducts, 'server'); } catch (err) {}
          });
        }
      } catch (err: any) {
        console.error("Direct fallback products fetch note:", err);
        if (onError) onError(err);
      }
    }
  }, 4000);

  return () => {
    isCancelled = true;
    if (safetyTimer) {
      clearTimeout(safetyTimer);
      safetyTimer = null;
    }
    productSubscribers.delete(onUpdate);
  };
}

export function resolveProductImageReference(img: string, currentProductId?: string): string {
  if (!img || typeof img !== 'string') return '';
  const trimmed = img.trim();
  if (!trimmed.startsWith('/api/product-image/')) return trimmed;

  const parts = trimmed.split('/');
  const sourceId = parts[3];
  const sourceIdx = parseInt(parts[4] || '0', 10) || 0;

  if (!sourceId) return trimmed;

  // 1. Try to find raw image in lastProductData
  if (lastProductData && Array.isArray(lastProductData)) {
    const found = lastProductData.find(p => p && p.id === sourceId);
    if (found) {
      const previews = Array.isArray(found.previewImages) ? found.previewImages : (found.image ? [found.image] : []);
      const cand = previews[sourceIdx] || previews[0];
      if (cand && typeof cand === 'string' && (cand.startsWith('data:image/') || cand.startsWith('http'))) {
        return cand;
      }
    }
  }

  // 2. Try to find in initialProductsData
  if (Array.isArray(initialProductsData)) {
    const rawProd = (initialProductsData as any[]).find(p => p && p.id === sourceId);
    if (rawProd) {
      const rawPreviews = Array.isArray(rawProd.previewImages) ? rawProd.previewImages : (rawProd.image ? [rawProd.image] : []);
      const cand = rawPreviews[sourceIdx] || rawPreviews[0] || rawProd.image;
      if (cand && typeof cand === 'string' && (cand.startsWith('data:image/') || cand.startsWith('http'))) {
        return cand;
      }
    }
  }

  // 3. Try to find in localStorage cache
  if (typeof window !== 'undefined') {
    try {
      const cached = localStorage.getItem(CACHE_KEY) || localStorage.getItem('rh_cached_products');
      if (cached) {
        const arr = JSON.parse(cached);
        if (Array.isArray(arr)) {
          const found = arr.find((p: any) => p && p.id === sourceId);
          if (found) {
            const rawPreviews = Array.isArray(found.previewImages) ? found.previewImages : (found.image ? [found.image] : []);
            const cand = rawPreviews[sourceIdx] || rawPreviews[0];
            if (cand && typeof cand === 'string' && (cand.startsWith('data:image/') || cand.startsWith('http'))) {
              return cand;
            }
          }
        }
      }
    } catch {}
  }

  return trimmed;
}

export async function saveProductToFirestore(product: Product, isNew: boolean = false): Promise<void> {
  const previousProductData = lastProductData ? [...lastProductData] : null;
  try {
    const finalUrl = (product as any).downloadFileUrl || (product as any).downloadUrl || (product as any).driveUrl || (product as any).fileUrl || '';
    
    // 1. Resolve previewImages and image to real raw data if they are proxy URLs
    const incomingPreviews = Array.isArray(product.previewImages) && product.previewImages.length > 0
      ? [...product.previewImages]
      : (product.image ? [product.image] : []);

    let resolvedPreviews = incomingPreviews.map((im, idx) => resolveProductImageReference(im, product.id));

    // If still proxy and we have access to Firestore document, attempt to read existing raw images from Firestore
    if (resolvedPreviews.some(img => typeof img === 'string' && img.startsWith('/api/product-image/'))) {
      const firstProxy = resolvedPreviews.find(img => typeof img === 'string' && img.startsWith('/api/product-image/'));
      const parts = firstProxy ? firstProxy.split('/') : [];
      const lookupId = (isNew && parts[3] && parts[3] !== product.id) ? parts[3] : product.id;
      try {
        const docSnap = await getDoc(doc(db, PRODUCTS_COLLECTION, lookupId));
        if (docSnap.exists()) {
          const dData = docSnap.data();
          const docPreviews = Array.isArray(dData?.previewImages) ? dData.previewImages : (dData?.image ? [dData.image] : []);
          if (docPreviews.length > 0 && docPreviews.some((im: any) => typeof im === 'string' && (im.startsWith('data:image/') || im.startsWith('http')))) {
            resolvedPreviews = resolvedPreviews.map((im, idx) => {
              if (typeof im === 'string' && im.startsWith('/api/product-image/')) {
                return docPreviews[idx] || docPreviews[0] || im;
              }
              return im;
            });
          }
        }
      } catch {}
    }

    // Deduplicate previews using canonical image keys to guarantee the main cover is never duplicated
    const seenResolved = new Set<string>();
    resolvedPreviews = resolvedPreviews.filter(im => {
      if (!im || typeof im !== 'string') return false;
      const key = getImageDeduplicationKey(im);
      if (seenResolved.has(key)) return false;
      seenResolved.add(key);
      return true;
    });

    const resolvedImage = resolvedPreviews[0]
      || resolveProductImageReference(product.image || '', product.id)
      || '';

    // Product object to store, keeping secure attributes and synchronized image arrays
    const productToPersist: Product = {
      ...product,
      previewImages: resolvedPreviews,
      image: resolvedImage,
      images: resolvedPreviews,
      downloadFileUrl: finalUrl,
      hasSecureFile: Boolean(finalUrl && !finalUrl.includes('sample_zip'))
    };

    const nowIso = new Date().toISOString();
    const optimisticProduct: Product = {
      ...productToPersist,
      updatedAt: nowIso,
      createdAt: isNew ? (product.createdAt || nowIso) : (product.createdAt || nowIso)
    };

    unmarkProductAsDeleted(product.id);

    // 1. Instant optimistic in-memory catalog update preserving ALL existing products
    let currentCatalog = (lastProductData && lastProductData.length > 0)
      ? [...lastProductData]
      : (previousProductData && previousProductData.length > 0)
      ? [...previousProductData]
      : mergeWithBaseCatalog([]);

    const existingIndex = currentCatalog.findIndex(p => p.id === optimisticProduct.id);
    let bumpedProds: Product[];
    if (existingIndex >= 0) {
      currentCatalog[existingIndex] = optimisticProduct;
      bumpedProds = currentCatalog;
    } else {
      bumpedProds = [optimisticProduct, ...currentCatalog];
    }

    lastProductData = bumpedProds;
    safeCacheProducts(bumpedProds);
    broadcastProductsMutation(bumpedProds);

    // Notify all UI listeners immediately
    productSubscribers.forEach(cb => {
      try { cb(lastProductData!, 'server'); } catch (e) {}
    });

    const payload: any = sanitizeFirestoreData(productToPersist);
    // CRITICAL: NEVER delete image, previewImages, images from payload!
    // Always persist authoritative image data to Firestore so other clients/browsers never lose images!
    payload.image = resolvedImage;
    payload.previewImages = resolvedPreviews;
    payload.images = resolvedPreviews;
    payload.updatedAt = serverTimestamp();
    if (isNew) {
      payload.createdAt = serverTimestamp();
    }
    const isPub = (productToPersist.status || '').toLowerCase() === 'published' || (productToPersist.status || '').toLowerCase() === 'active';
    payload.isPublished = isPub;
    payload.published = isPub;
    if (!isPub) {
      delete payload.publishedAt;
      delete payload.publishedDate;
      delete payload.publishDate;
    }
    
    // Direct client Firestore write
    let clientSaveSucceeded = false;
    let backendSaveSucceeded = false;
    let persistenceError: any = null;

    try {
      await setDoc(doc(db, PRODUCTS_COLLECTION, product.id), payload, { merge: true });
      clientSaveSucceeded = true;
    } catch (clientErr) {
      console.warn('Client Firestore direct write note (will persist via backend Admin SDK):', clientErr);
      persistenceError = clientErr;
    }
    
    // Save secure download link separately in Firestore
    if (finalUrl) {
      try {
        await setDoc(doc(db, 'product_downloads', product.id), sanitizeFirestoreData({ url: finalUrl }), { merge: true });
      } catch (dlErr) {
        console.warn('Direct Firestore product_downloads save note:', dlErr);
      }
    }

    // Backend synchronization
    let authHeader = '';
    try {
      if (auth.currentUser) {
        authHeader = `Bearer ${await auth.currentUser.getIdToken()}`;
      }
    } catch {}

    const reqHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Admin-Access': 'true'
    };
    if (authHeader) {
      reqHeaders['Authorization'] = authHeader;
    }

    const syncPayload = {
      ...optimisticProduct,
      firestoreAlreadyWritten: clientSaveSucceeded
    };

    if (clientSaveSucceeded) {
      // Background notify backend without blocking UI, keeping server in-memory catalog warm
      fetch(isNew ? '/api/products' : `/api/products/${encodeURIComponent(product.id)}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: reqHeaders,
        body: JSON.stringify(syncPayload)
      }).catch((apiErr) => {
        console.warn('Background proxy product sync note:', apiErr);
      });

      if (finalUrl) {
        fetch(`/api/admin/product-download/${encodeURIComponent(product.id)}`, {
          method: 'PUT',
          headers: reqHeaders,
          body: JSON.stringify({ url: finalUrl })
        }).catch(() => {});
      }
    } else {
      // Fallback: Await backend to persist via Firebase Admin SDK
      try {
        if (finalUrl) {
          fetch(`/api/admin/product-download/${encodeURIComponent(product.id)}`, {
            method: 'PUT',
            headers: reqHeaders,
            body: JSON.stringify({ url: finalUrl })
          }).catch(() => {});
        }

        const apiRes = await fetch(isNew ? '/api/products' : `/api/products/${encodeURIComponent(product.id)}`, {
          method: isNew ? 'POST' : 'PUT',
          headers: reqHeaders,
          body: JSON.stringify(syncPayload)
        });
        if (apiRes.ok) {
          backendSaveSucceeded = true;
        } else {
          const errJson = await apiRes.json().catch(() => ({}));
          persistenceError = new Error(errJson?.message || `Server returned HTTP ${apiRes.status}`);
        }
      } catch (apiErr) {
        console.warn('Backend proxy product save fallback note:', apiErr);
        if (!persistenceError) persistenceError = apiErr;
      }

      if (!backendSaveSucceeded) {
        // Rollback optimistic update
        if (previousProductData) {
          lastProductData = previousProductData;
          safeCacheProducts(previousProductData);
          broadcastProductsMutation(previousProductData);
          productSubscribers.forEach(cb => {
            try { cb(previousProductData, 'server'); } catch (e) {}
          });
        }
        throw new Error(`Failed to persist product update to cloud database: ${persistenceError?.message || 'Network or authorization failure'}`);
      }
    }

  } catch (error) {
    console.error('Error saving product to Firestore:', error);
    throw error;
  }
}

export async function removeProductFromFirestore(productId: string): Promise<void> {
  try {
    markProductAsDeleted(productId);

    // 1. Instant 0ms optimistic in-memory catalog deletion
    if (lastProductData && Array.isArray(lastProductData)) {
      lastProductData = lastProductData.filter(p => p.id !== productId);
      safeCacheProducts(lastProductData);
      productSubscribers.forEach(cb => {
        try { cb(lastProductData!, 'server'); } catch (e) {}
      });
      broadcastProductsMutation(lastProductData);
    }

    // 2. Permanently delete product document from Firestore
    try {
      await deleteDoc(doc(db, PRODUCTS_COLLECTION, productId));
    } catch (delErr) {
      console.warn('Client direct deleteDoc note (will delete via backend Admin SDK):', delErr);
    }

    // 3. Delete secure download link from Firestore
    try {
      await deleteDoc(doc(db, 'product_downloads', productId));
    } catch {}

    // 4. Synchronously permanently delete via Express backend Admin SDK
    try {
      await fetch(`/api/products/${encodeURIComponent(productId)}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Access': 'true' }
      });
    } catch {}
  } catch (error) {
    console.error('Error deleting product from Firestore:', error);
    throw error;
  }
}

export function getProductCreatedTimestamp(p?: Product | null): number {
  if (!p) return 0;
  const parseVal = (val: any): number => {
    if (!val) return 0;
    if (typeof val === 'number') {
      return val < 1e11 ? val * 1000 : val;
    }
    if (typeof val === 'string') {
      const parsed = new Date(val).getTime();
      return !isNaN(parsed) && parsed > 0 ? parsed : 0;
    }
    if (typeof val === 'object') {
      if (typeof val.toDate === 'function') {
        const d = val.toDate();
        return d instanceof Date ? d.getTime() : 0;
      }
      if (typeof val.seconds === 'number') {
        return val.seconds * 1000;
      }
    }
    return 0;
  };

  // 1. Authoritative createdAt timestamp (when product was uploaded/published)
  const createdTime = parseVal(p.createdAt);

  // 2. Authoritative upload millisecond timestamp encoded in product ID (e.g. rh-muv72ter-4hly9)
  let idTime = 0;
  if (typeof p.id === 'string' && p.id.startsWith('rh-')) {
    try {
      const parts = p.id.split('-');
      if (parts[1]) {
        const parsedBase36 = parseInt(parts[1], 36);
        if (!isNaN(parsedBase36) && parsedBase36 > 1000000000000) {
          idTime = parsedBase36;
        }
      }
    } catch {}
  }

  // If both exist, use the higher/more precise timestamp (createdAt or idTime)
  const baseCreated = (createdTime > 0 && idTime > 0)
    ? Math.max(createdTime, idTime)
    : (createdTime || idTime);

  // 3. Check updatedAt: If product was explicitly edited/republished by admin
  // (cutoff: updates after 2026-10-09 bulk seed), bump to top of the list!
  const updatedTime = parseVal(p.updatedAt);
  const BULK_SEED_CUTOFF = 1760054400000; // 2026-10-09T00:00:00.000Z

  if (updatedTime > BULK_SEED_CUTOFF && updatedTime > baseCreated) {
    return updatedTime;
  }

  if (baseCreated > 0) return baseCreated;
  if (updatedTime > 0) return updatedTime;

  return 0;
}
